import { Injectable } from '@angular/core';
import FileSaver from 'file-saver';
import { Observable } from 'rxjs';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { finalize, map } from 'rxjs/operators';
import { HttpService } from './http.service';
import { HttpUrlEncodingCodec, NOT_CANCELLED_HEADER } from '../interceptors/auth.interceptor';
import { IStandEquipmentRequest } from '../models/standEquipmentRequest';
import { ITableExcelData } from '../models/table';
import { DwUsersActivityService } from './dw-users-activity.service';
import { DwUserActivityNames } from '../models/dwUserActivityNames';
import { DwUserActivityTypes } from '../models/dwUsersActivityTypes';

const enum Extensions {
	Pdf = '.pdf',
	Excel = '.xlsx'
}

export const enum AnalyticsReportNames {
	Complex = 'chain-sales',
	LaidProducts = 'laid-products',
	ProductHoles = 'complex-product-holes'
}

@Injectable({
	providedIn: 'root'
})
export class ExcelService {

	constructor(
		private http: HttpClient,
		private httpService: HttpService,
		private dwUsersActivityService: DwUsersActivityService,
	) {}

	private _saveFromString(response: Blob, name: string, type: Extensions): void {
		const blob = new Blob([response], { type: 'application/octet-stream' });
		FileSaver.saveAs(blob, name + type);
	}

	exportActiveProducts(params: any, name: string): Observable<void> {
		const headers = new HttpHeaders().set(NOT_CANCELLED_HEADER, 'true');
		return this.http.get('/api/v1/reports/active-products.xlsx/', {
			params: new HttpParams({
				fromObject: params,
				encoder: new HttpUrlEncodingCodec()
			}),
			responseType: 'blob',
			headers
		}).pipe(
			map((response: Blob) => this._saveFromString(response, name, Extensions.Excel))
		);
	}

	exportAsExcelFromServer(data, version_2?): Promise<any> {
		const headers = new HttpHeaders().set(NOT_CANCELLED_HEADER, 'true');
		return this.httpService.getPromise<any>(
			this.http
				.post<any>('/api/analytics/save-excel/' + (version_2 ? '?version=2' : ''), data, { headers })
				.pipe(map((response: any) => this.save(response.url, data.name, Extensions.Excel)))
		);
	}

    exportAsExcelFromServer$(data: any, version_2: boolean = false): Observable<any> {
		const headers = new HttpHeaders().set(NOT_CANCELLED_HEADER, 'true');
		return this.http
            .post<any>('/api/analytics/save-excel/' + (version_2 ? '?version=2' : ''), data, { headers })
            .pipe(map((response: any) => this.save(response.url, data.name, Extensions.Excel)));
	}

	exportComplexAnalytics(data: ITableExcelData, name: string): Observable<any> {
		const headers = new HttpHeaders().set(NOT_CANCELLED_HEADER, 'true');
		return this.http
			.post('/api/v1/reports/excel/', data, {
				params: new HttpParams({ encoder: new HttpUrlEncodingCodec() }),
				responseType: 'blob',
				headers
			})
			.pipe(map((response: Blob) => this._saveFromString(response, name, Extensions.Excel)));
	}

	exportExpandedAnalytics(
		reportName: AnalyticsReportNames,
		params: any,
		data: any,
		name: string
	): Observable<any> {
		return this.http
			.post(`/api/v1/reports/${reportName}/expanded-excel/`, data, {
				params: new HttpParams({
					encoder: new HttpUrlEncodingCodec(),
					fromObject: { ...params, notCancelled: true }
				}),
				responseType: 'blob'
			})
			.pipe(map((response: Blob) => this._saveFromString(response, name, Extensions.Excel)));
	}

	exportEquipmentsTemplates(name: string): Observable<void> {
		const headers = new HttpHeaders().set(NOT_CANCELLED_HEADER, 'true');
		return this.http
			.get('/api/v1/equipment-templates/', { responseType: 'blob', headers })
			.pipe(map((response: Blob) => this._saveFromString(response, name, Extensions.Excel)));
	}

	exportInactiveProducts(params: any, name: string): Observable<void> {
		const headers = new HttpHeaders().set(NOT_CANCELLED_HEADER, 'true');
		return this.http.get('/api/v1/reports/inactive-products.xlsx/', {
			params: new HttpParams({
				fromObject: params,
				encoder: new HttpUrlEncodingCodec()
			}),
			responseType: 'blob',
			headers
		}).pipe(
			map((response: Blob) => this._saveFromString(response, name, Extensions.Excel))
		);
	}

	exportPlanCompletion(params: any, name: string, image: string, planId: number): Observable<void> {
		const headers = new HttpHeaders().set(NOT_CANCELLED_HEADER, 'true');
		return this.http
			.post(
				'/api/v1/reports/plan-completion/excel.xlsx/',
				{
					image: image
				},
				{
					params: new HttpParams({
						fromObject: {
							plan_id: planId,
							...params
						},
						encoder: new HttpUrlEncodingCodec()
					}),
					responseType: 'blob',
					headers
				}
			)
			.pipe(map((response: Blob) => this._saveFromString(response, name, Extensions.Excel)));
	}

	exportProductHoles(params: any, name: string): Observable<void> {
		const headers = new HttpHeaders().set(NOT_CANCELLED_HEADER, 'true');
		return this.http.get('/api/v1/reports/product-holes.xlsx/', {
			params: new HttpParams({
				fromObject: params,
				encoder: new HttpUrlEncodingCodec()
			}),
			responseType: 'blob',
			headers
		}).pipe(
			map((response: Blob) => this._saveFromString(response, name, Extensions.Excel))
		);
	}

	exportProductHolesPrediction(params: any, name: string): Observable<void> {
		const headers = new HttpHeaders().set(NOT_CANCELLED_HEADER, 'true');
		return this.http.get('/api/v1/reports/product-holes-prediction.xlsx/', {
			params: new HttpParams({
				fromObject: params,
				encoder: new HttpUrlEncodingCodec()
			}),
			responseType: 'blob',
			headers
		}).pipe(
			map((response: Blob) => this._saveFromString(response, name, Extensions.Excel))
		);
	}

	exportStandEquipment(data: IStandEquipmentRequest): Observable<void> {
		if (data?.search_fields) {
			data.search_fields = [data.search_fields, 'block_name'].join(',');
		}
		const id: number = data.id;
		delete data.id;

		const headers = new HttpHeaders().set(NOT_CANCELLED_HEADER, 'true');
		return this.http
			.post(`/api/layout/stands/${id}/equipment/?version=2`, data, {
				params: new HttpParams({ encoder: new HttpUrlEncodingCodec() }),
				responseType: 'blob',
				headers
			})
			.pipe(
				map((response: Blob) => this._saveFromString(response, data.name, data.equipment_format === 'excel' ? Extensions.Excel : Extensions.Pdf))
			);
	}

	exportUnLaidProducts(params: any, name: string): Observable<void> {
		const headers = new HttpHeaders().set(NOT_CANCELLED_HEADER, 'true');
		return this.http.get('/api/v1/reports/not-laid-products.xlsx/', {
			params: new HttpParams({
				fromObject: params,
				encoder: new HttpUrlEncodingCodec()
			}),
			responseType: 'blob',
			headers
		}).pipe(
			map((response: Blob) => this._saveFromString(response, name, Extensions.Excel))
		);
	}

	getMPProducts(id: number, name: string, params: any = {}): Observable<void> {
		const headers = new HttpHeaders().set(NOT_CANCELLED_HEADER, 'true');
		return this.http.get(`/api/v1/master-products/${id}/products-table.xlsx/`, {
			params: new HttpParams({
				fromObject: params,
				encoder: new HttpUrlEncodingCodec()
			}),
			responseType: 'blob',
			headers
		}).pipe(
			finalize(() => this.dwUsersActivityService.track(DwUserActivityNames.Products, DwUserActivityTypes.DownloadData)),
			map((response: Blob) => this._saveFromString(response, name, Extensions.Excel))
		);
	}

	getProductsLayout(params: any, name: string, id: number): Observable<void> {
		const headers = new HttpHeaders().set(NOT_CANCELLED_HEADER, 'true');
		return this.http.get(`/api/v1/master-products/${id}/usage.xlsx/`, {
			params: new HttpParams({
				fromObject: params,
				encoder: new HttpUrlEncodingCodec()
			}),
			responseType: 'blob',
			headers
		}).pipe(
			map((response: Blob) => this._saveFromString(response, name, Extensions.Excel))
		);
	}

	productsToExcel(params: any, name: string): Observable<void> {
		const headers = new HttpHeaders().set(NOT_CANCELLED_HEADER, 'true');
		return this.http.get('/api/v1/master-products-table.xlsx/', {
			params: new HttpParams({
				fromObject: params,
				encoder: new HttpUrlEncodingCodec()
			}),
			responseType: 'blob',
			headers
		}).pipe(
			map((response: Blob) => this._saveFromString(response, name, Extensions.Excel))
		);
	}

	save(link: string, filename: string, fileType: string): Promise<any> {
		const xhr = new XMLHttpRequest();
		xhr.responseType = 'blob';

		const promise = new Promise<void>(((resolve, reject) => {
			xhr.onload = function () {
				try {
					let a = document.createElement('a');
					a.href = window.URL.createObjectURL(xhr.response); // xhr.response is a blob
					a.download = filename + fileType; // Set the file name.
					a.style.display = 'none';
					document.body.appendChild(a);
					a.click();
					a.remove();
					resolve();
				} catch (err) {
					reject(err);
				}
			};
		}));

		xhr.open('GET', link);
		xhr.send();

		return promise;
	}
}